import {
  LayoutDashboard, Rocket, Zap, Users, Settings,
  Archive, Activity, User, CreditCard, Bell, HelpCircle, Blocks,
  ChevronRight, Wand2
} from 'lucide-react';
import { useState } from 'react';
import styles from './Navigation.module.css';

function Navigation({ currentView, onViewChange, user, packages, unreadCount = 0 }) {
  const [forceHide, setForceHide] = useState(null);
  const [mobileOpenSubmenu, setMobileOpenSubmenu] = useState(null);

  const navSections = [
    {
      label: 'General',
      items: [
        { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'developer'], badge: null },
        { id: 'activity', label: 'Activity', icon: Zap, roles: ['admin', 'developer'], badge: null },
      ]
    },
    {
      label: 'Platform',
      items: [
        { id: 'deploy', label: 'Deploy App', icon: Rocket, roles: ['admin', 'developer'], badge: null },
        { id: 'deploy-ai-deploy', label: 'AI Auto-Deploy', icon: Wand2, roles: ['admin', 'developer'] },
        { id: 'integrations', label: 'Integrations', icon: Blocks, roles: ['admin', 'developer'], badge: 'Under Dev' },
        { id: 'backups', label: 'Backups', icon: Archive, roles: ['admin', 'developer'], badge: 'Under Dev' },
      ]
    },
    {
      label: 'Organization',
      items: [
        { id: 'stats', label: 'Resource Usage', icon: Activity, roles: ['admin', 'developer'], badge: null },
        { id: 'admin', label: 'Team Management', icon: Users, roles: ['admin'], badge: null },
      ]
    },
    {
      label: 'Settings',
      items: [
        { id: 'profile', label: 'Profile', icon: User, roles: ['admin', 'developer'], badge: null },
        { id: 'billing', label: 'Billing & Subscription', icon: CreditCard, roles: ['admin', 'developer'], badge: null, requiresOwner: true },
        { id: 'notifications', label: 'Notifications', icon: Bell, roles: ['admin', 'developer'], badge: unreadCount > 0 ? (unreadCount > 99 ? '99+' : unreadCount) : null, isAlertBadge: unreadCount > 0 },
        { id: 'settings', label: 'General Settings', icon: Settings, roles: ['admin', 'developer'], badge: null },
        { id: 'support', label: 'Support', icon: HelpCircle, roles: ['admin', 'developer'], badge: null },
      ]
    }
  ];

  return (
    <nav className={styles.mainNavContainer}>
      <div className={styles.mainNav}>
        {navSections.map((section, idx) => {
          const userTier = String(user?.packageTier || '').toLowerCase();
          const isPaidPlan = userTier.includes('pro') || userTier.includes('agency') || userTier.includes('enterprise') || userTier.includes('custom') || user?.role === 'admin';

          const activePkg = (packages || []).find(p => {
            const pTier = String(p?.tierId || p?.id || p?.tier || '').toLowerCase();
            return pTier === userTier || (isPaidPlan && pTier.includes('pro'));
          });

          const maxTeamMembers = activePkg?.limits?.maxTeamMembers ?? (isPaidPlan ? 5 : 0);

          const filteredItems = section.items.filter(item => {
            // Team Management (item.id === 'admin') is only accessible to Admin or SuperAdmin users
            if (item.id === 'admin' && user.role !== 'admin' && user.role !== 'superadmin') {
                return false;
            }

            // First: Can they access this item?
            let hasAccess = false;
            
            if (item.roles.includes(user.role)) {
                hasAccess = true;
            } else if (item.roles.includes('admin') && (user.role === 'admin' || user.role === 'superadmin') && user.isOwner && maxTeamMembers > 0) {
                // Owner with a package that allows team members can access admin items
                hasAccess = true;
            }
            
            if (!hasAccess) return false;
            if (item.requiresOwner && !user.isOwner && user.role !== 'admin') return false;
            
            // Second: Dynamic hiding based on quota
            // Hide Team Management if their package does not allow team members (unless they are superadmin)
            if (item.id === 'admin' && maxTeamMembers <= 0 && user.role !== 'superadmin') {
                return false;
            }
            
            return true;
          });
          if (filteredItems.length === 0) return null;

          return (
            <div key={idx} className={styles.navSection}>
              {section.label && <div className={styles.navSectionLabel}>{section.label}</div>}
              {filteredItems.map(view => {
                const Icon = view.icon;
                const hasSubItems = Boolean(view.subItems && view.subItems.length > 0);
                const isDeployItem = view.id === 'deploy' && (currentView === 'deploy' || (String(currentView).startsWith('deploy-') && currentView !== 'deploy-ai-deploy') || currentView === 'auto-deploy');
                const isBillingItem = view.id === 'billing' && (currentView === 'billing' || currentView === 'plans');
                const isActive = isDeployItem || isBillingItem || currentView === view.id || (hasSubItems && view.subItems.some(sub => currentView === sub.id));
                const isMobileSubmenuOpen = mobileOpenSubmenu === view.id;

                return (
                  <div key={view.id} className={`${styles.navItemWrapper} ${isActive ? styles.active : ''}`}>
                    <button
                      className={`${styles.navItem} ${isActive ? styles.active : ''}`}
                      onClick={() => {
                        if (hasSubItems && window.innerWidth <= 768) {
                          setMobileOpenSubmenu(prev => prev === view.id ? null : view.id);
                          return;
                        }

                        onViewChange(view.id);
                      }}
                      aria-expanded={hasSubItems ? isMobileSubmenuOpen : undefined}
                    >
                      <span className={styles.navIcon}><Icon size={18} strokeWidth={2.0} /></span>
                      <span className={styles.navLabel}>{view.label}</span>
                      {hasSubItems && (
                        <span className={styles.navExpandIcon}>
                          <ChevronRight size={14} />
                        </span>
                      )}
                      {view.badge && <span className={`${styles.navBadge} ${view.isAlertBadge ? styles.alertBadge : ''}`}>{view.badge}</span>}
                    </button>

                    {hasSubItems && (
                      <div className={`nav-sub-items ${forceHide === view.id ? 'force-hide' : ''} ${isMobileSubmenuOpen ? 'mobile-open' : ''}`}>
                        {view.subItems.map(sub => {
                          const SubIcon = sub.icon;
                          return (
                            <button
                              key={sub.id}
                              className={`nav-sub-item ${currentView === sub.id ? 'active' : ''}`}
                              onClick={() => {
                                onViewChange(sub.id);
                                setForceHide(view.id);
                                setMobileOpenSubmenu(null);
                                setTimeout(() => setForceHide(null), 500);
                              }}
                            >
                              <span className="nav-sub-icon"><SubIcon size={14} strokeWidth={2.0} /></span>
                              <span className="nav-sub-label">{sub.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </nav>
  );
}

export default Navigation;
